import { ticks } from 'd3-array';
import { area, curveLinear, curveMonotoneX, line, type CurveFactory } from 'd3-shape';

import type {
  ChartPadding,
  ChartPlot,
  ChartTick,
  ChartTradeMarker,
  ChartTradeMarkerGroup,
} from '../../types';
import { createLinearScale, type LinearScale } from '../../utils/scales';
import type {
  TimeSeriesAreaCurve,
  TimeSeriesAreaData,
  TimeSeriesCandlestickData,
  TimeSeriesChartLabels,
  TimeSeriesData,
} from '../types';
import { formatTimeSeriesTimestamp } from './formatters';
import { getTimeSeriesExtent } from './scales';

const BAR_HEIGHT_RATIO = 0.2;
const BAR_WIDTH_RATIO = 0.7;
const MAX_BAR_WIDTH = 40;
const CANDLE_WIDTH_RATIO = 0.65;
const MAX_CANDLE_WIDTH = 40;
const MIN_CANDLE_WIDTH = 1;
const MIN_CANDLE_BODY_HEIGHT = 1;
const VALUE_DOMAIN_PADDING_RATIO = 0.05;
const MINUTE_IN_MILLIS = 60 * 1000;
const MINUTES_IN_DAY = 24 * 60;
const TICK_STEPS_IN_MINUTES = [
  5,
  10,
  15,
  30,
  60,
  120,
  180,
  240,
  360,
  720,
  ...[1, 2, 3, 7, 14, 30, 60, 90, 120, 180, 270, 365, 545, 730, 1095, 1460].map(
    (days) => days * MINUTES_IN_DAY,
  ),
];
// Keeps centred labels clear of the plot edges and the value labels column.
const LABEL_EDGE_MARGIN = 28;

export const TRADE_MARKER_BODY_SIZE = 16;
export const TRADE_MARKER_BODY_RADIUS = 4;
export const TRADE_MARKER_POINTER_HEIGHT = 5;
export const TRADE_MARKER_ANCHOR_OFFSET = 4;
export const TRADE_MARKER_SNAP_THRESHOLD = 12;
export const TRADE_MARKER_MERGE_DISTANCE = 10;

const AREA_CURVES: Record<TimeSeriesAreaCurve, CurveFactory> = {
  monotoneX: curveMonotoneX,
  linear: curveLinear,
};

export type TimeSeriesChartContext<T extends TimeSeriesData> = {
  data: T[];
  plot: ChartPlot;
  xDomain: [number, number];
  yDomain: [number, number];
  /** Value domain before the volume-bar buffer is applied; the basis for value-axis zooming. */
  valueDomain: [number, number];
  xScale: LinearScale;
  yScale: LinearScale;
};

/** Finds the point nearest to a timestamp in an ascending time series. */
export const findClosestTimeSeriesPoint = <T extends TimeSeriesData>(
  data: T[],
  timestamp: number,
): T | undefined => {
  if (data.length === 0) return undefined;

  let lowerIndex = 0;
  let upperIndex = data.length - 1;

  while (lowerIndex < upperIndex) {
    const middleIndex = Math.floor((lowerIndex + upperIndex) / 2);

    if (data[middleIndex].timestamp < timestamp) {
      lowerIndex = middleIndex + 1;
    } else {
      upperIndex = middleIndex;
    }
  }

  const nextPoint = data[lowerIndex];
  const previousPoint = data[lowerIndex - 1];

  if (!previousPoint) return nextPoint;

  return timestamp - previousPoint.timestamp <= nextPoint.timestamp - timestamp
    ? previousPoint
    : nextPoint;
};

/** Anchor value of the series at a pixel position, interpolated between the bracketing points. */
const interpolateAnchorValueAtX = <T extends TimeSeriesData>(
  { data, xScale }: TimeSeriesChartContext<T>,
  getAnchorValue: (point: T) => number,
  x: number,
): number => {
  const nextIndex = data.findIndex((point) => xScale(point.timestamp) >= x);
  if (nextIndex <= 0) {
    return getAnchorValue(data[nextIndex === 0 ? 0 : data.length - 1]);
  }

  const previousPoint = data[nextIndex - 1];
  const nextPoint = data[nextIndex];
  const previousX = xScale(previousPoint.timestamp);
  const nextX = xScale(nextPoint.timestamp);
  const previousValue = getAnchorValue(previousPoint);
  const nextValue = getAnchorValue(nextPoint);
  if (nextX === previousX) return nextValue;

  return previousValue + ((nextValue - previousValue) * (x - previousX)) / (nextX - previousX);
};

export type TradeMarkerGroupGeometry = ChartTradeMarkerGroup & {
  x: number;
  y: number;
};

/** Snaps trade markers to the nearest series point and merges pixel-adjacent buckets into groups. */
export const createTradeMarkerGeometry = <T extends TimeSeriesData>(
  context: TimeSeriesChartContext<T>,
  markers: ChartTradeMarker[],
  getAnchorValue: (point: T) => number,
  getMaxAnchorValue: (point: T) => number = getAnchorValue,
): TradeMarkerGroupGeometry[] => {
  const buckets = new Map<number, { point: T; markers: ChartTradeMarker[] }>();
  // The newest point is the plot's right edge, so a trade made inside the in-progress
  // bucket sits past it. Admit one bucket's worth and let the x clamp pin it to the edge.
  const { data } = context;
  const trailingStep =
    data.length > 1 ? data[data.length - 1].timestamp - data[data.length - 2].timestamp : 0;
  const maxTimestamp = context.xDomain[1] + Math.max(trailingStep, 0);

  markers.forEach((marker) => {
    if (
      !Number.isFinite(marker.timestamp) ||
      !Number.isFinite(marker.tokenAmount) ||
      marker.timestamp < context.xDomain[0] ||
      marker.timestamp > maxTimestamp
    ) {
      return;
    }

    const point = findClosestTimeSeriesPoint(context.data, marker.timestamp);
    if (!point) return;

    const bucket = buckets.get(point.timestamp);
    if (bucket) {
      bucket.markers.push(marker);
    } else {
      buckets.set(point.timestamp, { point, markers: [marker] });
    }
  });

  const sortedBuckets = Array.from(buckets.values())
    .map((bucket) => ({ ...bucket, snappedX: context.xScale(bucket.point.timestamp) }))
    .toSorted((bucketA, bucketB) => bucketA.snappedX - bucketB.snappedX);

  const mergedBuckets: (typeof sortedBuckets)[] = [];
  sortedBuckets.forEach((bucket) => {
    const currentGroup = mergedBuckets[mergedBuckets.length - 1];
    const lastBucket = currentGroup?.[currentGroup.length - 1];

    if (lastBucket && bucket.snappedX - lastBucket.snappedX < TRADE_MARKER_MERGE_DISTANCE) {
      currentGroup.push(bucket);
    } else {
      mergedBuckets.push([bucket]);
    }
  });

  const { plot, xScale, yScale } = context;

  return mergedBuckets.map((groupBuckets) => {
    const groupMarkers = groupBuckets
      .flatMap(({ markers: bucketMarkers }) => bucketMarkers)
      .toSorted(
        (markerA, markerB) =>
          markerA.timestamp - markerB.timestamp || markerA.key.localeCompare(markerB.key),
      );
    const meanX =
      groupMarkers.reduce((sum, marker) => sum + xScale(marker.timestamp), 0) / groupMarkers.length;
    const x = Math.min(
      Math.max(meanX, plot.left + TRADE_MARKER_BODY_SIZE / 2),
      plot.right - TRADE_MARKER_BODY_SIZE / 2,
    );
    // Point at the traded price, clamped between the series directly under the
    // marker and its high: a fill under the bucketed close would otherwise sink
    // into the line, and a fill premium would float past the wick.
    const seriesY = yScale(interpolateAnchorValueAtX(context, getAnchorValue, x));
    const capY = yScale(interpolateAnchorValueAtX(context, getMaxAnchorValue, x));
    const tradeValues = groupMarkers.map(({ tooltipValue }) => tooltipValue);
    const tradeY = tradeValues.every(
      (value): value is number => value != null && Number.isFinite(value),
    )
      ? yScale(tradeValues.reduce((sum, value) => sum + value, 0) / tradeValues.length)
      : seriesY;
    const anchorY = Math.min(Math.max(tradeY, capY), seriesY);
    const rawY =
      anchorY -
      TRADE_MARKER_ANCHOR_OFFSET -
      TRADE_MARKER_POINTER_HEIGHT -
      TRADE_MARKER_BODY_SIZE / 2;
    const y = Math.min(
      Math.max(rawY, plot.top + TRADE_MARKER_BODY_SIZE / 2),
      plot.bottom - TRADE_MARKER_BODY_SIZE / 2 - TRADE_MARKER_POINTER_HEIGHT,
    );
    const direction = groupMarkers.every(({ direction }) => direction === groupMarkers[0].direction)
      ? groupMarkers[0].direction
      : 'mixed';

    return {
      key: groupMarkers.map(({ key }) => key).join('|'),
      markers: groupMarkers,
      direction,
      count: groupMarkers.length,
      x,
      y,
    };
  });
};

export const findTradeMarkerGroupAtX = (
  geometry: TradeMarkerGroupGeometry[],
  x: number,
): TradeMarkerGroupGeometry | undefined => {
  return geometry.reduce<{ group?: TradeMarkerGroupGeometry; distance: number }>(
    (closest, group) => {
      const distance = Math.abs(group.x - x);
      return distance <= TRADE_MARKER_SNAP_THRESHOLD && distance < closest.distance
        ? { group, distance }
        : closest;
    },
    { distance: Number.POSITIVE_INFINITY },
  ).group;
};

type CreateTimeSeriesChartContextOptions<T extends TimeSeriesData> = {
  data: T[];
  width: number;
  height: number;
  padding: ChartPadding;
  timestampDomain?: [number, number];
  getMinValue: (point: T) => number;
  getMaxValue?: (point: T) => number;
  valueDomain?: [number, number];
  valueFloor?: number;
  showBars?: boolean;
};

/** Validates chart data and creates the shared plot dimensions, domains and scales. */
export const createTimeSeriesChartContext = <T extends TimeSeriesData>({
  data,
  width,
  height,
  padding,
  timestampDomain,
  getMinValue,
  getMaxValue = getMinValue,
  valueDomain,
  valueFloor,
  showBars = false,
}: CreateTimeSeriesChartContextOptions<T>): TimeSeriesChartContext<T> | undefined => {
  const validData = data.filter(
    (point) =>
      isFinite(point.timestamp) &&
      isFinite(getMinValue(point)) &&
      isFinite(getMaxValue(point)) &&
      (timestampDomain == null ||
        (point.timestamp >= timestampDomain[0] && point.timestamp <= timestampDomain[1])),
  );

  const extent = getTimeSeriesExtent(validData, getMinValue, getMaxValue);

  const { top, right, bottom, left } = padding;
  const plotWidth = Math.max(width - left - right, 0);
  const plotHeight = Math.max(height - top - bottom, 0);

  if (!extent || plotWidth === 0 || plotHeight === 0) return undefined;

  const { minTimestamp, maxTimestamp, minValue, maxValue } = extent;
  const xDomain: [number, number] = timestampDomain ?? [minTimestamp, maxTimestamp];
  const valueRange = maxValue - minValue;
  const valuePadding = valueRange * VALUE_DOMAIN_PADDING_RATIO;

  // Pad inferred domains to keep extrema off the plot edges.
  const [domainMinValueDefault, domainMaxValue]: [number, number] = valueDomain ?? [
    minValue - valuePadding,
    maxValue + valuePadding,
  ];

  const domainMinValue =
    valueFloor == null ? domainMinValueDefault : Math.max(domainMinValueDefault, valueFloor);

  // Expand the domain instead of shortening the scale so value labels can use the full plot height.
  const bottomDomainBuffer = showBars
    ? (domainMaxValue - domainMinValue) * (BAR_HEIGHT_RATIO / (1 - BAR_HEIGHT_RATIO))
    : 0;

  // Only apply the bottom domain buffer when it does not extend below the value floor.
  const applyBottomDomainBuffer =
    valueFloor == null || domainMinValue - bottomDomainBuffer >= valueFloor;

  const domainMinValueBuffered = applyBottomDomainBuffer
    ? domainMinValue - bottomDomainBuffer
    : domainMinValue;

  const yDomain: [number, number] = [domainMinValueBuffered, domainMaxValue];
  const plot = {
    top,
    right: left + plotWidth,
    bottom: top + plotHeight,
    left,
    width: plotWidth,
    height: plotHeight,
  };

  return {
    data: validData,
    plot,
    xDomain,
    yDomain,
    valueDomain: [domainMinValue, domainMaxValue],
    xScale: createLinearScale(xDomain, [plot.left, plot.right]),
    yScale: createLinearScale(yDomain, [plot.bottom, plot.top]),
  };
};

/**
 * Creates ticks pinned to clock and calendar times (aligned to local midnight) so labels stay put
 * as the domain moves, and midnight itself is always a tick on intraday ranges.
 */
const createTimeSeriesTicks = (
  [minTimestamp, maxTimestamp]: [number, number],
  count: number,
  plot: ChartPlot,
  xScale: LinearScale,
  format: (timestamp: number) => string,
): ChartTick[] => {
  const range = maxTimestamp - minTimestamp;
  // Ticks at both edges get trimmed below, so budget two extra to land near the requested count.
  const stepInMinutes =
    TICK_STEPS_IN_MINUTES.find((step) => range / (step * MINUTE_IN_MILLIS) <= count + 2) ??
    TICK_STEPS_IN_MINUTES[TICK_STEPS_IN_MINUTES.length - 1];
  const stepInDays = stepInMinutes / MINUTES_IN_DAY;
  const isMultiDayStep = Number.isInteger(stepInDays);
  // Sub-day steps all divide a day, so anchoring them to the window's own midnight already keeps
  // the lattice fixed. Multi-day steps would slide by a day whenever that midnight changes, so
  // they count from a fixed Monday (Jan 3, 2000) instead.
  const anchor = isMultiDayStep
    ? new Date(2000, 0, 3).getTime()
    : new Date(minTimestamp).setHours(0, 0, 0, 0);
  // Stepping local date fields rather than elapsed milliseconds keeps labels on round clock times
  // and on whole days when the range crosses a daylight-saving shift.
  const getTickTimestamp = (index: number) => {
    const date = new Date(anchor);
    if (isMultiDayStep) {
      date.setDate(date.getDate() + index * stepInDays);
    } else {
      date.setMinutes(date.getMinutes() + index * stepInMinutes);
    }
    return date.getTime();
  };

  const ticks: ChartTick[] = [];
  const firstIndex = Math.floor((minTimestamp - anchor) / (stepInMinutes * MINUTE_IN_MILLIS));

  for (let index = firstIndex; ; index += 1) {
    const timestamp = getTickTimestamp(index);
    if (timestamp > maxTimestamp) break;
    if (timestamp < minTimestamp) continue;

    const position = xScale(timestamp);
    if (position < plot.left + LABEL_EDGE_MARGIN || position > plot.right - LABEL_EDGE_MARGIN) {
      continue;
    }
    ticks.push({ position, label: format(timestamp), anchor: 'middle' });
  }

  // Domains shorter than the smallest step can contain no aligned tick; label the midpoint instead.
  if (ticks.length === 0) {
    const midpoint = (minTimestamp + maxTimestamp) / 2;
    return [{ position: xScale(midpoint), label: format(midpoint), anchor: 'middle' }];
  }

  return ticks;
};

export const createTimeSeriesChartTicks = <T extends TimeSeriesData>(
  { plot, xDomain, yDomain, xScale, yScale }: TimeSeriesChartContext<T>,
  labels: TimeSeriesChartLabels | undefined,
) => {
  if (!labels) return { xTicks: [], yTicks: [] };

  const [minTimestamp, maxTimestamp] = xDomain;
  const [domainMinValue, domainMaxValue] = yDomain;
  const xLabelsCount = plot.width < 480 ? 3 : 5;
  const xRange = maxTimestamp - minTimestamp;
  const xLabelsFormatter = (timestamp: number) => formatTimeSeriesTimestamp(timestamp, xRange);
  const xTicks = createTimeSeriesTicks(xDomain, xLabelsCount, plot, xScale, xLabelsFormatter);

  const minTickValue = Math.max(domainMinValue, labels.minTickValue ?? domainMinValue);
  const labelAreaHeight = yScale(minTickValue) - plot.top;
  const yLabelsCount = labelAreaHeight < plot.height ? 4 : 8;
  // Round tick values keep labels stable while the value domain is panned or zoomed.
  const yTicks = ticks(minTickValue, domainMaxValue, yLabelsCount).map((value) => ({
    position: yScale(value),
    label: labels.formatValue(value),
  }));

  return { xTicks, yTicks };
};

export type BarGeometry = {
  timestamp: number;
  x: number;
  y: number;
  width: number;
  height: number;
};

/** Converts finite non-negative bar values into rectangles within the plot bounds. */
export const createBarGeometry = <T extends TimeSeriesData>({
  data,
  plot,
  xScale,
}: TimeSeriesChartContext<T>): BarGeometry[] => {
  const barData = data.flatMap(({ timestamp, barValue }) =>
    barValue != null && Number.isFinite(barValue) && barValue >= 0
      ? [{ timestamp, value: barValue }]
      : [],
  );
  const extent = getTimeSeriesExtent(barData, ({ value }) => value);
  if (!extent || extent.maxValue <= 0) return [];

  const barWidth = Math.max(
    Math.min((plot.width / Math.max(data.length, 1)) * BAR_WIDTH_RATIO, MAX_BAR_WIDTH),
    1,
  );
  const barScale = createLinearScale(
    [0, extent.maxValue],
    [plot.bottom, plot.bottom - plot.height * BAR_HEIGHT_RATIO],
  );

  return barData.map(({ timestamp, value }) => {
    const barHeight = plot.bottom - barScale(value);
    const x = Math.min(
      Math.max(xScale(timestamp) - barWidth / 2, plot.left),
      plot.right - barWidth,
    );

    return { timestamp, x, y: plot.bottom - barHeight, width: barWidth, height: barHeight };
  });
};

export const createAreaGeometry = (
  { data, plot, xScale, yScale }: TimeSeriesChartContext<TimeSeriesAreaData>,
  curve: TimeSeriesAreaCurve,
) => {
  const lineGenerator = line<TimeSeriesAreaData>()
    .x(({ timestamp }) => xScale(timestamp))
    .y(({ value }) => yScale(value))
    .curve(AREA_CURVES[curve]);
  const areaGenerator = area<TimeSeriesAreaData>()
    .x(({ timestamp }) => xScale(timestamp))
    .y0(plot.bottom)
    .y1(({ value }) => yScale(value))
    .curve(AREA_CURVES[curve]);

  return {
    areaPath: areaGenerator(data) ?? '',
    linePath: lineGenerator(data) ?? '',
  };
};

export type CandlestickGeometry = {
  point: TimeSeriesCandlestickData;
  x: number;
  wickTop: number;
  wickBottom: number;
  bodyX: number;
  bodyY: number;
  bodyWidth: number;
  bodyHeight: number;
};

export const createCandlestickGeometry = ({
  data,
  plot,
  xScale,
  yScale,
}: TimeSeriesChartContext<TimeSeriesCandlestickData>): CandlestickGeometry[] => {
  const bodyWidth = Math.max(
    Math.min((plot.width / Math.max(data.length, 1)) * CANDLE_WIDTH_RATIO, MAX_CANDLE_WIDTH),
    MIN_CANDLE_WIDTH,
  );

  return data.map((point) => {
    const x = xScale(point.timestamp);
    const openY = yScale(point.open);
    const closeY = yScale(point.close);
    const rawBodyHeight = Math.abs(closeY - openY);
    const bodyHeight = Math.max(rawBodyHeight, MIN_CANDLE_BODY_HEIGHT);
    const bodyY = Math.min(openY, closeY) - (bodyHeight - rawBodyHeight) / 2;
    const bodyX = Math.min(Math.max(x - bodyWidth / 2, plot.left), plot.right - bodyWidth);

    return {
      point,
      x,
      wickTop: yScale(point.high),
      wickBottom: yScale(point.low),
      bodyX,
      bodyY,
      bodyWidth,
      bodyHeight,
    };
  });
};
